/**
 * W3C Trace Context propagation.
 *
 * Parsing inbound `traceparent` and injecting it outbound is what lets a span
 * join an existing trace instead of appearing as an orphan root: browser →
 * Worker, Worker → generation worker → lok-pdf-service, and generation worker →
 * the API's completion callback.
 *
 * Note the inbound `sampled` flag is NOT trusted by default — see
 * `trustRemoteSampling` in http.ts.
 *
 * https://www.w3.org/TR/trace-context/
 */

import { isValidSpanId, isValidTraceId } from './ids';

export type SpanContext = {
	traceId: string;
	spanId: string;
	/** Bit 0 of trace-flags: the upstream sampling decision. */
	sampled: boolean;
	/** Opaque vendor state, forwarded unmodified when present. */
	traceState?: string;
};

/** `version-traceid-spanid-flags`, all lowercase hex. */
const TRACEPARENT_RE = /^([0-9a-f]{2})-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/;

/**
 * Parse a `traceparent` header. Returns null for anything malformed — a bad
 * header must start a fresh trace, never throw into the request path.
 */
export function parseTraceparent(header: string | null | undefined, traceState?: string | null): SpanContext | null {
	if (!header) return null;
	const match = TRACEPARENT_RE.exec(header.trim().toLowerCase());
	if (!match) return null;

	const [, version, traceId, spanId, flags] = match as unknown as [string, string, string, string, string];

	// Version ff is explicitly forbidden. Higher versions must be accepted
	// forwards-compatibly, so only ff is rejected.
	if (version === 'ff') return null;
	if (!isValidTraceId(traceId) || !isValidSpanId(spanId)) return null;

	return {
		traceId,
		spanId,
		sampled: (parseInt(flags, 16) & 0x01) === 0x01,
		traceState: traceState?.trim() || undefined,
	};
}

/** Format a span context as a `traceparent` header value (version 00). */
export function formatTraceparent(ctx: Pick<SpanContext, 'traceId' | 'spanId' | 'sampled'>): string {
	return `00-${ctx.traceId}-${ctx.spanId}-${ctx.sampled ? '01' : '00'}`;
}

/**
 * Inject trace context into an outbound header bag.
 *
 * Accepts anything with a `set` method (Headers) or a plain record, so it works
 * for both `fetch(url, { headers })` shapes used across the codebase.
 */
export function injectTraceContext(
	headers: Headers | Record<string, string>,
	ctx: Pick<SpanContext, 'traceId' | 'spanId' | 'sampled'> & { traceState?: string },
): void {
	const traceparent = formatTraceparent(ctx);
	if (headers instanceof Headers) {
		headers.set('traceparent', traceparent);
		if (ctx.traceState) headers.set('tracestate', ctx.traceState);
	} else {
		headers['traceparent'] = traceparent;
		if (ctx.traceState) headers['tracestate'] = ctx.traceState;
	}
}
